import React from 'react';
import {
	STOPPAGE_EVENTS_ACTIVITY_OPTIONS,
	BALLAST_LADEN_OPTIONS,
	IAdditionalFilter,
	ReportType,
	PORT_CALLS_ACTIVITY_OPTIONS,
} from '../../types/reportsTypes';
import { DateRangeLocationFilter } from './components/DateRangeLocationFilter';
import { Select, SelectItem } from '@heroui/react';
import { NumberRange } from '../../../../shared/components/number-range/NumberRange';

interface IAdditionalFiltersProps {
	filters: IAdditionalFilter;
	maxDate?: Date; // Optional prop for dynamic date handling
	minDate?: Date; // Optional prop for current date handling
	onChange: (filters: IAdditionalFilter) => void;
	reportType?: ReportType; // Optional prop to adjust available filters based on report type
}

export const AdditionalFilters: React.FC<IAdditionalFiltersProps> = ({
	filters,
	onChange,
	maxDate,
	minDate,
	reportType,
}) => {
	const updateFilters = <K extends keyof IAdditionalFilter>(key: K, value: IAdditionalFilter[K] | null) => {
		if (value === null) {
			return;
		}
		onChange({
			...filters,
			[key]: value,
		});
	};

	// Determine which activity options to use based on report type
	const activityOptions =
		reportType === ReportType.StoppageEvents ? STOPPAGE_EVENTS_ACTIVITY_OPTIONS : PORT_CALLS_ACTIVITY_OPTIONS;

	return (
		<div className="flex flex-col h-full gap-2">
			<h2 className="text-xl font-semibold text-msi-midblue-color-500 border-b">Additional Filters</h2>

			<div className="flex flex-col space-y-2 overflow-auto w-full p-1">
				{filters.dateRange && filters.location && (
					<DateRangeLocationFilter
						dateRange={filters.dateRange}
						location={filters.location}
						maxDate={maxDate}
						minDate={minDate}
						onDateRangeChange={(value) => updateFilters('dateRange', value)}
						onLocationChange={(value) => updateFilters('location', value)}
						reportType={reportType}
					/>
				)}

				{filters.originDateRangeAndLocation && (
					<DateRangeLocationFilter
						title="Origin"
						dateRange={filters.originDateRangeAndLocation.dateRange}
						location={filters.originDateRangeAndLocation.location}
						maxDate={maxDate}
						onDateRangeChange={(value) =>
							updateFilters('originDateRangeAndLocation', { ...filters.originDateRangeAndLocation, dateRange: value! })
						}
						onLocationChange={(value) =>
							updateFilters('originDateRangeAndLocation', { ...filters.originDateRangeAndLocation, location: value! })
						}
						reportType={reportType}
					/>
				)}

				{filters.destinationDateRangeAndLocation && (
					<DateRangeLocationFilter
						title="Destination"
						dateRange={filters.destinationDateRangeAndLocation.dateRange}
						location={filters.destinationDateRangeAndLocation.location}
						maxDate={maxDate}
						onDateRangeChange={(value) =>
							updateFilters('destinationDateRangeAndLocation', { ...filters.destinationDateRangeAndLocation, dateRange: value! })
						}
						onLocationChange={(value) =>
							updateFilters('destinationDateRangeAndLocation', { ...filters.destinationDateRangeAndLocation, location: value! })
						}
						reportType={reportType}
					/>
				)}

				{filters.duration && (
					<div className="flex flex-row w-full items-center">
						<span className="text-sm font-medium text-msi-midblue-color-500 w-1/3">Duration (Days):</span>
						<div className="flex items-center w-2/3">
							<NumberRange
								fromValue={filters.duration.min}
								toValue={filters.duration.max}
								onFromChange={(value) => {
									updateFilters('duration', {
										...filters.duration!,
										min: value === '' ? null : Number(value),
									});
								}}
								onToChange={(value) => {
									updateFilters('duration', {
										...filters.duration!,
										max: value === '' ? null : Number(value),
									});
								}}
							/>
						</div>
					</div>
				)}

				{filters.ballastLaden && (
					<div className="flex flex-row w-full items-center">
						<span className="text-sm font-medium text-msi-midblue-color-500 w-1/3">Ballast/Laden:</span>
						<div className="flex items-center w-2/3">
							<Select
								selectedKeys={filters.ballastLaden.value ? [filters.ballastLaden.value] : []}
								onSelectionChange={(keys) => {
									const selectedKeys = Array.from(keys) as string[];
									if (selectedKeys.length === 0) {
										updateFilters('ballastLaden', {
											value: '',
											label: '',
										});
										return;
									}

									const selected = BALLAST_LADEN_OPTIONS.find((opt) => opt.key === selectedKeys[0]);
									updateFilters('ballastLaden', {
										value: selected?.key || '',
										label: selected?.label || '',
									});
								}}
								placeholder="Ballast/Laden Select"
								size="sm"
								selectionMode="single"
								aria-label="Ballast/Laden Select"
							>
								{BALLAST_LADEN_OPTIONS.map((option) => (
									<SelectItem key={option.key} textValue={option.label}>
										{option.label}
									</SelectItem>
								))}
							</Select>
						</div>
					</div>
				)}

				{filters.designDraft && (
					<div className="flex flex-row w-full items-center">
						<span className="text-sm font-medium text-msi-midblue-color-500 w-1/3">% Design Draft:</span>
						<div className="flex items-center w-2/3">
							<NumberRange
								fromValue={filters.designDraft.min}
								toValue={filters.designDraft.max}
								onFromChange={(value) => {
									updateFilters('designDraft', {
										...filters.designDraft!,
										min: value === '' ? null : Number(value),
									});
								}}
								onToChange={(value) => {
									updateFilters('designDraft', {
										...filters.designDraft!,
										max: value === '' ? null : Number(value),
									});
								}}
							/>
						</div>
					</div>
				)}

				{filters.activity && (
					<div className="flex flex-row w-full items-center">
						<span className="text-sm font-medium text-msi-midblue-color-500 w-1/3">Activity:</span>
						<div className="flex items-center w-2/3">
							<Select
								id="activity-select"
								selectedKeys={filters.activity.value ? filters.activity.value.split(',') : []}
								onSelectionChange={(keys) => {
									const selectedKeys = Array.from(keys) as string[];
									if (selectedKeys.length === 0) {
										updateFilters('activity', {
											value: '',
											label: '',
										});
										return;
									}

									const selectedOptions = activityOptions.filter((opt) => selectedKeys.includes(opt.key));
									updateFilters('activity', {
										value: selectedKeys.join(','),
										label: selectedOptions.map((opt) => opt.label).join(', '),
									});
								}}
								placeholder="Select Activity"
								size="sm"
								selectionMode="multiple"
								className="rounded-none"
								aria-label="Select Activity"
							>
								{activityOptions.map((option) => (
									<SelectItem key={option.key} textValue={option.label}>
										{option.label}
									</SelectItem>
								))}
							</Select>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};
